import { getText } from '../translations/translations.js';
import { createIconMarkup } from './icon.js';
import { MENU_SETTINGS } from '/settings.js';

const GUIDE_STORAGE_KEY = 'app_guide_seen';

/**
 * Check if user has already seen the guide
 */
export function hasSeenGuide() {
    const val = localStorage.getItem(GUIDE_STORAGE_KEY);
    if (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) {
        console.log('[Guide Debug] hasSeenGuide() localStorage value:', val);
    }
    return val === 'true';
}

/**
 * Mark guide as seen
 */
function markGuideAsSeen() {
    localStorage.setItem(GUIDE_STORAGE_KEY, 'true');
}

/**
 * Show the guide overlay with step-by-step spotlight
 */

// --- Guide/App/Cookie Consent Synchronization ---
let __guideAppLoaded = false;
let __guideCookieConsentGiven = false;
let __guideShowPending = false;
let __guideAlreadyShown = false;
let __guideManualTriggerInFlight = false;

function checkAndShowGuide() {
    if (__guideAlreadyShown) return;

    // Respect the domain-level flag for automatic startup guide
    // Default to true if not specified for backward compatibility
    const autoShowEnabled = MENU_SETTINGS?.SHOW_GUIDE_ON_STARTUP !== false;

    if (!autoShowEnabled) {
        if (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) {
            console.log('[Guide Debug] checkAndShowGuide: SHOW_GUIDE_ON_STARTUP is false, skipping automatic show.');
        }
        __guideShowPending = false;
        return;
    }

    if (__guideAppLoaded && __guideCookieConsentGiven && !hasSeenGuide()) {
        __guideShowPending = false;
        actuallyShowGuide();
    } else if (__guideAppLoaded && __guideCookieConsentGiven && hasSeenGuide()) {
        __guideShowPending = false;
    } else {
        __guideShowPending = true;
    }
}

export async function notifyGuideAppLoaded() {
    __guideAppLoaded = true;
    checkAndShowGuide();
}

export async function notifyGuideCookieConsentGiven() {
    __guideCookieConsentGiven = true;
    checkAndShowGuide();
}

export async function showGuide() {
    // Called by app.js after UI is ready
    if (__guideAppLoaded) return; // Prevent double
    __guideAppLoaded = true;
    // Check cookie consent
    let consent = false;
    try {
        const { isCookieConsentAccepted } = await import('../info/cookies-utils.js');
        consent = isCookieConsentAccepted();
    } catch { }
    if (consent || window.__FORCE_GUIDE_FROM_MENU__) {
        __guideCookieConsentGiven = true;
    } else {
        // Listen for event if not yet given
        window.addEventListener('cookieConsentAccepted', () => {
            __guideCookieConsentGiven = true;
            checkAndShowGuide();
        }, { once: true });
    }
    checkAndShowGuide();
}

async function actuallyShowGuide() {
    if (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) {
        console.log('[Guide Debug] actuallyShowGuide() called. __guideAlreadyShown:', __guideAlreadyShown);
    }
    if (__guideAlreadyShown && !window.__FORCE_GUIDE_FROM_MENU__) {
        if (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) {
            console.log('[Guide Debug] actuallyShowGuide: already shown, skipping.');
        }
        return;
    }
    // Only block if automatic and already seen
    if (hasSeenGuide() && !window.__FORCE_GUIDE_FROM_MENU__) {
        if (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) {
            console.log('[Guide Debug] hasSeenGuide() is true. Guide will NOT be shown (automatic trigger).');
        }
        return;
    } else {
        if (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) {
            console.log('[Guide Debug] Proceeding to show guide.');
        }
    }
    // Remove any existing guide overlay before creating a new one
    const existingOverlay = document.querySelector('.guide-overlay');
    if (existingOverlay) {
        existingOverlay.remove();
    }
    // Set flag immediately to prevent race conditions
    __guideAlreadyShown = true;
    // Create overlay at the top before any usage
    const overlay = document.createElement('div');
    overlay.className = 'guide-overlay';

    // Get translations
    const guideTitle = await getText('guide.title');
    const stepTitle0 = await getText('guide.stepTitle0');
    const stepTitle1 = await getText('guide.stepTitle1');
    const stepTitle2 = await getText('guide.stepTitle2');
    const stepTitle3 = await getText('guide.stepTitle3');
    const stepTitle4 = await getText('guide.stepTitle4');
    const stepTitle5 = await getText('guide.stepTitle5');
    const introText = await getText('guide.intro');
    const menuText = await getText('guide.menuButton');
    const exploreText = await getText('guide.exploreButton');
    const actionButtonsText = await getText('guide.actionButtons');
    const cardsText = await getText('guide.cards');
    const favoritesText = await getText('guide.favorites');
    const startButtonText = await getText('guide.startButton');
    const nextButtonText = await getText('guide.nextButton');
    const backButtonText = await getText('guide.backButton');
    const gotItButtonText = await getText('guide.gotItButton');

    // Define guide steps with positioning config
    // direction: 'bottom' | 'top' | 'left' | 'right' - where to position card relative to target
    // offset: distance in pixels from target (default 20px)
    const steps = [
        {
            target: null,
            title: stepTitle0,
            text: introText,
            highlightPadding: 0,
            offset: { desktop: 0, mobile: 0 },
            width: { desktop: 400, mobile: 340 }
            // No direction for first step - always centered
        },
        {
            target: '#menu-toggle',
            title: stepTitle1,
            text: menuText,
            highlightPadding: 8,
            direction: 'bottom',
            offset: { desktop: 100, mobile: 100 },
            width: { desktop: 400, mobile: 280 },
            // Manual adjustment: move right by 24px on desktop, 0px on mobile
            offsetX: { desktop: 0, mobile: 24 },
            offsetY: { desktop: 0, mobile: 0 }
        },
        {
            target: '.header-action-btn.explore',
            title: stepTitle2,
            text: exploreText,
            highlightPadding: 8,
            direction: 'bottom',
            offset: { desktop: 100, mobile: 100 },
            width: { desktop: 400, mobile: 320 }
        },
        {
            target: '#map-action-buttons',
            title: stepTitle3,
            text: actionButtonsText,
            highlightPadding: 12,
            direction: 'left',
            offset: { desktop: 20, mobile: 12 },
            width: { desktop: 400, mobile: 330 }
        },
        {
            target: '#spots-cards-banner',
            title: stepTitle4,
            text: cardsText,
            highlightPadding: 12,
            direction: 'top',
            offset: { desktop: 100, mobile: 100 },
            width: { desktop: 400, mobile: 340 }
        }
        // Hidden for now - can be re-enabled in the future
        // {
        //     target: null,
        //     title: stepTitle5,
        //     text: favoritesText,
        //     highlightPadding: 0
        // }
    ];

    let currentStep = 0;

    // Create overlay (already created above)
    if (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) {
        console.log('[Guide Debug] Creating guide overlay and adding to DOM.');
    }

    // Create central text box
    const textBox = document.createElement('div');
    textBox.className = 'guide-text-box';

    const closeButton = document.createElement('button');
    closeButton.className = 'guide-close-btn';
    closeButton.innerHTML = '×';
    closeButton.setAttribute('aria-label', 'Close guide');

    const title = document.createElement('h3');
    title.className = 'guide-title';
    title.textContent = guideTitle;

    const textContent = document.createElement('p');
    textContent.className = 'guide-text-content';

    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'guide-buttons';

    const backButton = document.createElement('button');
    backButton.className = 'guide-back-btn guide-icon-btn';
    backButton.innerHTML = createIconMarkup('arrow-left-solid-full', 'guide-nav-icon');
    backButton.setAttribute('aria-label', backButtonText);

    const nextButton = document.createElement('button');
    nextButton.className = 'guide-next-btn guide-icon-btn';
    nextButton.innerHTML = createIconMarkup('arrow-right-solid-full', 'guide-nav-icon');
    nextButton.setAttribute('aria-label', startButtonText);

    buttonContainer.appendChild(backButton);
    buttonContainer.appendChild(nextButton);
    textBox.appendChild(closeButton);
    textBox.appendChild(title);
    textBox.appendChild(textContent);
    textBox.appendChild(buttonContainer);
    overlay.appendChild(textBox);

    // Add to DOM
    document.body.appendChild(overlay);

    // Close button handler
    closeButton.onclick = () => {
        closeGuide(overlay);
    };

    /**
     * Position the guide text box based on step configuration
     * @param {Object} step - The current step with direction config
     * @param {HTMLElement} targetElement - The element being highlighted (null for centered steps)
     */
    const positionGuideBox = (step, targetElement) => {
        // Get viewport size
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const isMobile = viewportWidth <= 600;

        // Helper to get value for desktop/mobile
        function getResponsiveValue(val, fallback) {
            if (typeof val === 'object' && val !== null) {
                return isMobile ? (val.mobile ?? fallback) : (val.desktop ?? fallback);
            }
            return val ?? fallback;
        }

        // Manual per-step offset support (offsetX, offsetY)
        const manualOffsetX = getResponsiveValue(step.offsetX, 0);
        const manualOffsetY = getResponsiveValue(step.offsetY, 0);

        if (!targetElement) {
            // Center position for intro step
            const desiredWidth = getResponsiveValue(step.width, 400);
            const widthToUse = Math.min(desiredWidth, viewportWidth - 32);
            textBox.style.left = '50%';
            textBox.style.top = '50%';
            textBox.style.transform = 'translate(-50%, -50%)';
            textBox.style.right = 'auto';
            textBox.style.bottom = 'auto';
            textBox.style.width = widthToUse + 'px';
            textBox.style.maxWidth = widthToUse + 'px';
            return;
        }

        // Get target element position
        const rect = targetElement.getBoundingClientRect();
        const direction = step.direction || 'bottom';
        const offset = getResponsiveValue(step.offset, 20);

        // Reset styles
        textBox.style.left = 'auto';
        textBox.style.right = 'auto';
        textBox.style.top = 'auto';
        textBox.style.bottom = 'auto';
        textBox.style.transform = 'none';
        textBox.style.width = '';

        // Position based on direction (using center-middle of guide box as reference point)
        const targetCenterX = rect.left + (rect.width / 2);
        const targetCenterY = rect.top + (rect.height / 2);

        // Allow per-step width override
        let desiredWidth = getResponsiveValue(step.width, 400);
        let widthToUse = desiredWidth;

        // Debug logs for troubleshooting
        if (
            (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) ||
            (typeof LOGGING !== 'undefined' && LOGGING.ENABLED && LOGGING.DEBUG)
        ) {
            console.log('[Guide Debug] direction:', direction, 'rect.left:', rect.left, 'rect.right:', rect.right, 'targetCenterX:', targetCenterX, 'desiredWidth:', desiredWidth, 'offset:', offset);
        }

        if (direction === 'bottom' || direction === 'top') {
            // Centered below/above target, clamp width to available viewport space
            widthToUse = Math.min(desiredWidth, viewportWidth - 32);
            // Apply manual offsetX (horizontal shift)
            const leftPos = targetCenterX + manualOffsetX;
            textBox.style.left = `${leftPos}px`;
            textBox.style.transform = 'translate(-50%, -50%)';
            if (direction === 'bottom') {
                textBox.style.top = `${rect.bottom + offset + manualOffsetY}px`;
            } else {
                textBox.style.top = `${rect.top - offset + manualOffsetY}px`;
            }
        } else if (direction === 'left') {
            // Grow left, right edge aligns with target
            // Only shrink if not enough space to the left
            widthToUse = Math.min(desiredWidth, rect.left - 16, viewportWidth - 32);
            textBox.style.left = `${rect.left - offset + manualOffsetX}px`;
            textBox.style.top = `${targetCenterY + manualOffsetY}px`;
            textBox.style.transform = `translate(-100%, -50%)`;
        } else if (direction === 'right') {
            // Grow right, left edge aligns with target
            widthToUse = Math.min(desiredWidth, viewportWidth - rect.right - 16, viewportWidth - 32);
            textBox.style.left = `${rect.right + offset + manualOffsetX}px`;
            textBox.style.top = `${targetCenterY + manualOffsetY}px`;
            textBox.style.transform = 'translate(0, -50%)';
        } else {
            // Fallback to bottom
            widthToUse = Math.min(desiredWidth, viewportWidth - 32);
            const leftPos = targetCenterX + manualOffsetX;
            textBox.style.left = `${leftPos}px`;
            textBox.style.top = `${rect.bottom + offset + manualOffsetY}px`;
            textBox.style.transform = 'translate(-50%, -50%)';
        }

        textBox.style.width = widthToUse + 'px';
        textBox.style.maxWidth = widthToUse + 'px';
    };

    // Show current step
    const showStep = (stepIndex) => {
        const step = steps[stepIndex];
        const targetElement = step.target ? document.querySelector(step.target) : null;

        // Position the guide box
        positionGuideBox(step, targetElement);

        // Remove previous highlight
        document.querySelectorAll('.guide-highlight').forEach(el => {
            el.classList.remove('guide-highlight');
            el.style.zIndex = '';

            // Also remove z-index from parent containers
            let parent = el.parentElement;
            while (parent && parent !== document.body) {
                if (parent.style.zIndex === '11000') {
                    parent.style.zIndex = '';
                }
                parent = parent.parentElement;
            }
        });

        // Handle spotlight based on step
        if (stepIndex === 0 && !targetElement) {
            // Intro (step 0 without target): remove any existing spotlight and apply a full dark background
            const existingSpotlight = overlay.querySelector('.guide-spotlight');
            if (existingSpotlight) {
                existingSpotlight.remove();
            }
            // Apply dark background class to overlay (simpler and more reliable than tiny spotlight)
            overlay.classList.add('guide-dark-bg');
        } else if (targetElement) {
            // Ensure dark-bg is removed when highlighting a target
            overlay.classList.remove('guide-dark-bg');
            // Other steps: create/show spotlight
            const existingSpotlight = overlay.querySelector('.guide-spotlight');
            if (existingSpotlight) {
                existingSpotlight.remove();
            }

            // Add highlight to current element
            targetElement.style.zIndex = '11000';
            targetElement.classList.add('guide-highlight');

            // Set z-index on parent containers to escape stacking context
            let parent = targetElement.parentElement;
            while (parent && parent !== document.body) {
                if (!parent.style.zIndex || parseInt(parent.style.zIndex) < 11000) {
                    parent.style.zIndex = '11000';
                }
                parent = parent.parentElement;
            }

            // Create spotlight effect
            createSpotlight(overlay, targetElement, step.highlightPadding || 10);
        }

        // Update title with step title
        title.textContent = step.title || guideTitle;

        // Update text and replace icon placeholders with SVG icons using createIconMarkup
        const iconRegex = /\{icon:([^}]+)\}/g;
        textContent.innerHTML = step.text.replace(iconRegex, (match, iconName) => {
            return createIconMarkup(iconName, 'guide-inline-icon');
        });

        // Update button icons and labels
        if (stepIndex === steps.length - 1) {
            nextButton.innerHTML = createIconMarkup('check-solid-full', 'guide-nav-icon');
            nextButton.setAttribute('aria-label', gotItButtonText);
        } else {
            nextButton.innerHTML = createIconMarkup('arrow-right-solid-full', 'guide-nav-icon');
            nextButton.setAttribute('aria-label', `${nextButtonText} (${stepIndex + 1}/${steps.length})`);
        }

        // Update back button visibility
        if (stepIndex === 0) {
            backButton.style.display = 'none';
        } else {
            backButton.style.display = '';
        }
    };

    // Next button handler
    nextButton.onclick = () => {
        if (currentStep < steps.length - 1) {
            currentStep++;
            showStep(currentStep);
        } else {
            closeGuide(overlay);
        }
    };

    // Back button handler
    backButton.onclick = () => {
        if (currentStep > 0) {
            currentStep--;
            showStep(currentStep);
        }
    };

    // Animate in and show first step
    setTimeout(() => {
        overlay.classList.add('active');
        showStep(0);
    }, 10);
}

/**
 * Wait for loading overlay to be hidden
 */
function waitForLoadingToComplete() {
    return new Promise(resolve => {
        const loading = document.getElementById('app-loading');

        if (!loading) {
            // No loading element, proceed immediately
            resolve();
            return;
        }

        // Check if loading is already hidden
        if (loading.style.display === 'none' || !loading.classList.contains('visible')) {
            resolve();
            return;
        }

        // Wait for loading to be hidden
        const observer = new MutationObserver(() => {
            if (loading.style.display === 'none' || !loading.classList.contains('visible')) {
                observer.disconnect();
                resolve();
            }
        });

        observer.observe(loading, {
            attributes: true,
            attributeFilter: ['style', 'class'],
            subtree: false
        });

        // Timeout after 5 seconds just in case
        setTimeout(() => {
            observer.disconnect();
            resolve();
        }, 5000);
    });
}

/**
 * Create spotlight effect around element
 */
function createSpotlight(overlay, element, padding) {
    // Remove existing spotlight
    const existingSpotlight = overlay.querySelector('.guide-spotlight');
    if (existingSpotlight) {
        existingSpotlight.remove();
    }

    const rect = element.getBoundingClientRect();
    const spotlight = document.createElement('div');
    spotlight.className = 'guide-spotlight';

    // Position spotlight around the element
    spotlight.style.top = `${rect.top - padding}px`;
    spotlight.style.left = `${rect.left - padding}px`;
    spotlight.style.width = `${rect.width + (padding * 2)}px`;
    spotlight.style.height = `${rect.height + (padding * 2)}px`;

    overlay.appendChild(spotlight);
}

/**
 * Close and remove guide
 */
function closeGuide(overlay) {
    // Remove highlights
    document.querySelectorAll('.guide-highlight').forEach(el => {
        el.classList.remove('guide-highlight');
        el.style.zIndex = '';

        // Also remove z-index from parent containers
        let parent = el.parentElement;
        while (parent && parent !== document.body) {
            if (parent.style.zIndex === '11000') {
                parent.style.zIndex = '';
            }
            parent = parent.parentElement;
        }
    });

    overlay.classList.remove('active');
    // Remove dark background class if present
    overlay.classList.remove('guide-dark-bg');
    setTimeout(() => {
        overlay.remove();
        markGuideAsSeen();
        // Dispatch custom event when guide closes
        document.dispatchEvent(new CustomEvent('guide-closed'));
    }, 300);
}


/**
 * Reset guide (for testing)
 */
export function resetGuide() {
    localStorage.removeItem(GUIDE_STORAGE_KEY);
}

/**
 * Show guide again (for menu button - clears the "seen" flag and shows guide)
 */
export function showGuideAgain() {
    if (__guideManualTriggerInFlight) {
        return;
    }
    __guideManualTriggerInFlight = true;
    console.log('[Guide Debug] showGuideAgain called. Forcing guide overlay, independent of startup logic.');
    // Remove any existing guide overlay before creating a new one
    const existingOverlay = document.querySelector('.guide-overlay');
    if (existingOverlay) {
        existingOverlay.remove();
    }
    // Reset in-memory flag so manual trigger always works
    __guideAlreadyShown = false;
    // Show the guide overlay directly, bypassing all flags and localStorage
    window.__FORCE_GUIDE_FROM_MENU__ = true;
    actuallyShowGuide();
    setTimeout(() => { delete window.__FORCE_GUIDE_FROM_MENU__; }, 1000);
    setTimeout(() => { __guideManualTriggerInFlight = false; }, 500);
}